import jsesc from "jsesc";
import { fetchApi } from "../../config/fetchConfig";
import { apiDomain } from "../../environmentVariables";
import I18n from "../../I18n";
import { buildRequestBody, alias } from "../../utils";
import { pushDialog } from "./dialogActions";

const push = (id, title, view, data, modalType) => ({
    type: "PUSH_MODAL",
    id,
    payload: {
        title,
        view,
        transition: "enter",
        data,
        modalType,
    },
});

const pop = (id) => ({
    type: "POP_MODAL",
    id,
});

const remove = (id) => ({
    type: "REMOVE_MODAL",
    id,
});

const startFetchingModalData = (id) => ({
    type: "FETCH_MODAL_DATA",
    id,
});

const startSavingModalData = (id) => ({
    type: "SAVE_MODAL_DATA",
    id,
});

const updateStatus = (id, status, errors) => ({
    type: "UPDATE_MODAL_STATUS",
    id,
    errors,
    status,
});

const updateData = (id, data, args, status, modalType) => ({
    type: "UPDATE_MODAL_DATA",
    id,
    args,
    status,
    payload: data,
});

const removeModalData = (id) => ({
    type: "REMOVE_MODAL_DATA",
    id,
});

const ticket = (data) => {
    return {
        type: "FRESHDESK_FORM_DATA",
        payload: {
            data,
        },
    };
};

const promises = {};

const DomainCredential = Object.freeze({
    Include: "include",
    SameOrigin: "same-origin",
});

export const pushModal =
    ({ title, data, args, view, modalType = "" }) =>
    (dispatch) => {
        const id = `MODAL_${Date.now()}`;
        dispatch(push(id, title, view, data, modalType));
        dispatch(updateData(id, data, args, "initial", modalType));

        return new Promise((resolve, reject) => {
            promises[id] = [resolve, reject];
        });
    };

export const updateModalData =
    ({ id, data }) =>
    (dispatch, getState) => {
        const currentData = getState().modalsData[id].data;
        dispatch(updateData(id, { ...(currentData || {}), ...(data || {}) }));
    };

export const updateModalStatus =
    ({ id, status, errors }) =>
    (dispatch) => {
        dispatch(updateStatus(id, status, errors));
    };

export const updateModalErrors =
    ({ id, data }) =>
    (dispatch, getState) => {
        const currentData = getState().modalsData[id].data;
        dispatch(updateData(id, { ...(currentData || {}), ...(data || {}) }));
    };

export const fetchModalData =
    ({ id, queries, mapper }) =>
    (dispatch, getState) => {
        const locale = getState().locale;
        const currentData = (getState().modalsData[id] || {}).data || {};

        if (queries) {
            dispatch(startFetchingModalData(id));
            const queriesBody = buildRequestBody(queries, locale);
            fetchApi("/graphql", queriesBody, {}, { dispatch, user: getState()?.user })
                .then((res) => res.json().catch(() => ({})))
                .then((res) => {
                    const data = (res || {}).data || {};
                    const dataObject = {
                        data: {
                            ...currentData,
                        },
                    };
                    mapper.forEach((prop, index) => {
                        const query = queries[index];
                        if (query) {
                            const queryAlias = alias({
                                resource: query.resource,
                                args: query.args,
                                locale,
                            });
                            dataObject[prop] = data[queryAlias];
                        }
                    });
                    dispatch(updateData(id, dataObject));
                })
                .catch((e) => {
                    console.log(jsesc(e));
                });
        }
    };

export const saveModalData = (id) => (dispatch, getState) => {
    const modalData = (getState().modalsData[id] || { data: {} }).data.data;
    if (modalData && modalData.__typename) {
        const modalName = modalData.__typename.toLowerCase();
        const queriesBody = {
            query: `mutation($${modalName}:${modalData.__typename}Input!,$projectId:String){createOrUpdate${modalData.__typename}(${modalName}:$${modalName},projectId:$projectId){id}}`,
            variables: {
                [modalName]: {
                    ...modalData,
                    __typename: undefined,
                },
                projectId: modalData.projectId,
            },
        };

        if (modalName === "ticket") {
            delete queriesBody.variables.projectId;
        }

        dispatch(startSavingModalData(id));
        fetchApi("/graphql", queriesBody, {}, { dispatch, user: getState()?.user })
            .then((res) => res.json().catch(() => ({})))
            .then((res) => {
                const data = res.data || {};
                const errors = res.errors || [{ message: "Unknown Error" }];
                if (
                    data &&
                    data[`createOrUpdate${modalData.__typename}`] &&
                    data[`createOrUpdate${modalData.__typename}`].id
                ) {
                    dispatch(pop(id));
                    dispatch(removeModalData(id));
                    if (promises[id] && promises[id][0]) {
                        promises[id][0]();
                    }
                    Reflect.deleteProperty(promises, id);
                } else {
                    dispatch(updateModalStatus({ id, status: "error", errors }));
                }
            });
    } else {
        if (modalData && !modalData.__typename) {
            console.error("Missing __typename from model data");
        }
        dispatch(pop(id));
        dispatch(removeModalData(id));
        if (promises[id] && promises[id][1]) {
            promises[id][1]();
        }
        Reflect.deleteProperty(promises, id);
    }
};

export const saveTicketModal = (id) => (dispatch, getState) => {
    const modalData = (getState().modalsData[id] || { data: {} }).data.data;
    const path = modalData && modalData.projectId ? modalData.projectId : "";

    if (modalData && modalData.__typename) {
        const querieTicket = {
            query: `mutation($p:String!,$tID:String!,$tLabel:String!,$u:Int!){create_ticket(targetProjectId:$p,ticketId:$tID,ticketLabel:$tLabel,userId:$u){id}}`,
            variables: {
                p: modalData.projectId,
                tID: modalData.ticketNumber,
                tLabel: modalData.ticketDescription,
                u: modalData.userId,
            },
        };

        fetchApi("/graphql", querieTicket, {}, { dispatch, user: getState()?.user })
            .then((res) => res.json().catch(() => ({})))
            .then((res) => {
                if (res && res.errors) {
                    const errors = res.errors;
                    dispatch(updateModalStatus({ id, status: "error", errors }));
                } else {
                    window.location.href = "/en/" + path + "/repository/activity/status";
                }
            });
    } else {
        if (modalData && !modalData.__typename) {
            console.error("Missing __typename from model data");
        }
        dispatch(pop(id));
        dispatch(removeModalData(id));
        if (promises[id] && promises[id][1]) {
            promises[id][1]();
        }
        Reflect.deleteProperty(promises, id);
    }
};

export const popModal = (id) => (dispatch) => {
    dispatch(pop(id));
    dispatch(removeModalData(id));
    if (promises[id] && promises[id][1]) {
        promises[id][1]();
    }
    Reflect.deleteProperty(promises, id);
};

export const removeModal = (id) => (dispatch) => {
    dispatch(remove(id));
};

export const submitDataFilesAction = (queriesBody, id) => (dispatch, getState) => {
    dispatch(startSavingModalData(id));
    fetchApi("/graphql", queriesBody, {}, { dispatch, user: getState()?.user })
        .then((res) => res.json().catch(() => ({})))
        .then((res) => {
            const data = res.data || {};
            const errors = res.errors || [{ message: "Unknown Error" }];
            if (
                data &&
                data[`data_file_submission_request`] &&
                data[`data_file_submission_request`].id
            ) {
                dispatch(pop(id));
                dispatch(removeModalData(id));
                if (promises[id] && promises[id][0]) {
                    promises[id][0]();
                }
                Reflect.deleteProperty(promises, id);
            } else {
                dispatch(updateModalStatus({ id, status: "error", errors }));
            }
        });
};
export const submitFilesModal = (id) => (dispatch, getState) => {
    const modalData = (getState().modalsData[id] || { data: {} }).data.data;
    if (modalData && modalData.__typename) {
        const queriesBody = {
            query: `mutation($datafilesubmission:DataFileSubmissionInput!,$projectId:String){data_file_submission_request(datafilesubmission:$datafilesubmission,projectId:$projectId){id}}`,
            variables: {
                datafilesubmission: {
                    dataFileIds: modalData.dataFileIds,
                    userPassword: modalData.userPassword,
                    submissionFilePeriod: modalData.period,
                    userId: modalData.userId,
                    validate: modalData.validate,
                    __typename: undefined,
                },
                projectId: modalData.projectId,
            },
        };
        if (
            modalData.filesWithProblems.length &&
            modalData.filePeriods &&
            modalData.filePeriods.find((elem) => elem !== parseInt(modalData.period)) !== undefined
        ) {
            dispatch(
                pushDialog("ConfirmDialog", I18n.translate`Continue Submission?`, {
                    title: I18n.translate`Some of the selected files have critical or high inconsistencies.`,
                    promptText: I18n.translate`Please confirm that you want to proceed!`,
                }),
            )
                .then(() => {
                    dispatch(
                        pushDialog("ConfirmDialog", I18n.translate`Continue Submission?`, {
                            title: I18n.translate`Some of the selected files have a different period than the one entered.`,
                            promptText: I18n.translate`Please confirm that you want to proceed!`,
                        }),
                    ).then(() => {
                        dispatch(submitDataFilesAction(queriesBody, id));
                    });
                })
                .catch(() => null);
        } else if (modalData.filesWithProblems.length) {
            dispatch(
                pushDialog("ConfirmDialog", I18n.translate`Continue Submission?`, {
                    title: I18n.translate`Some of the selected files have critical or high inconsistencies.`,
                    promptText: I18n.translate`Please confirm that you want to proceed!`,
                }),
            )
                .then(() => {
                    dispatch(submitDataFilesAction(queriesBody, id));
                })
                .catch(() => null);
        } else if (
            modalData.filePeriods &&
            modalData.filePeriods.find((elem) => elem !== parseInt(modalData.period)) !== undefined
        ) {
            dispatch(
                pushDialog("ConfirmDialog", I18n.translate`Period Warning?`, {
                    title: I18n.translate`Some of the selected files have a different period than the one entered.`,
                    promptText: I18n.translate`Please confirm that you want to proceed!`,
                }),
            )
                .then(() => {
                    dispatch(submitDataFilesAction(queriesBody, id));
                })
                .catch(() => null);
        } else {
            dispatch(submitDataFilesAction(queriesBody, id));
        }
    } else {
        if (modalData && !modalData.__typename) {
            console.error("Missing __typename from model data");
        }
        dispatch(pop(id));
        dispatch(removeModalData(id));
        if (promises[id] && promises[id][1]) {
            promises[id][1]();
        }
        Reflect.deleteProperty(promises, id);
    }
};

export const submitTicketFormData = (id) => (dispatch, getState) => {
    dispatch(ticket(id));
    const modalData = (getState().modalsData[id] || { data: {} }).data.data;
    const ticketData = new FormData();
    const { uploadedFiles, ...rest } = modalData || {};
    uploadedFiles.map((file) => ticketData.append("attachments", file));
    ticketData.append("ticketFieldsData", JSON.stringify(rest));
    dispatch(startSavingModalData(id));
    fetch(apiDomain + "/api/create_freshdesk_ticket", {
        method: "POST",
        credentials: apiDomain ? DomainCredential.Include : DomainCredential.SameOrigin,
        body: ticketData,
    })
        .then((response) => response.json().catch(() => ({})))
        .then((response) => {
            if (response && response.id) {
                dispatch(pop(id));
                dispatch(removeModalData(id));
                if (promises[id] && promises[id][0]) {
                    promises[id][0]();
                }
                Reflect.deleteProperty(promises, id);
            } else {
                dispatch(updateModalStatus({ id, status: "error", errors: response }));
            }
        })
        .catch(() => [
            {
                errors: [
                    {
                        message: `Unkown error!`,
                        type: "genericError",
                    },
                ],
            },
        ]);
};

export const submitAuditNoteFormData = (id) => (dispatch, getState) => {
    dispatch(startFetchingModalData(id));
    const modalData = (getState().modalsData[id] || { data: {} }).data.data;
    const auditNoteData = new FormData();
    auditNoteData.append("auditNoteFormData", JSON.stringify(modalData));
    // AUDIT NOTES CAN HAVE ATTACHMENTS!! NEED TO APPLY LOGIC LATER
    dispatch(startSavingModalData(id));
    fetch(apiDomain + "/api/submit_audit_note", {
        method: "POST",
        credentials: apiDomain ? DomainCredential.Include : DomainCredential.SameOrigin,
        body: auditNoteData,
    })
        .then((response) => response.json().catch(() => ({})))
        .then((response) => {
            if (response && response.id) {
                dispatch(pop(id));
                dispatch(removeModalData(id));
                if (promises[id] && promises[id][0]) {
                    promises[id][0]();
                }
                Reflect.deleteProperty(promises, id);
            } else {
                const errors = response.errors || [{ message: "Unknown Error" }];
                dispatch(updateModalStatus({ id, status: "error", errors }));
            }
        })
        .catch(() => {
            const errors = {
                errors: [
                    {
                        message: "Unkown Error!",
                    },
                ],
            };
            dispatch(updateModalStatus({ id, status: "error", errors }));
        });
};

export const resendInvite = (username, notify) => (dispatch, getState) => {
    const queriesBody = {
        query: `mutation($username: String!) {
            resendSSOInvite(username: $username) {
                sent
            }
        }`,
        variables: {
            username,
        },
    };
    fetchApi("/graphql", queriesBody, {}, { dispatch, user: getState()?.user })
        .then((res) => res.json().catch(() => ({})))
        .then((res) => {
            notify(res.data.resendSSOInvite.sent || false);
        })
        .catch(() => notify(false));
};
